import React from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  TrendingUp,
  Calculator,
  Wallet,
  Sparkles,
  GraduationCap,
  PlaneTakeoff,
  Mail,
  Download,
  ExternalLink,
} from "lucide-react";
import { PageHero, SectionEyebrow } from "@/components/ui-bits";
import { LeadCaptureModal } from "@/components/LeadCaptureModal";
import { useAuth } from "@/context/AuthContext";
import { openDownload } from "@/utils/openDownload";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const APPS = [
  {
    slug: "app-notaryleadengine",
    icon: TrendingUp,
    name: "NotaryLeadEngine",
    tagline: "Lead capture & follow-up automation",
    features: [
      "Auto-capture inbound notary leads from your website & ads",
      "Smart routing by service type — mobile, RON, signing agent, apostille",
      "Built-in SMS + email follow-up sequences",
      "Pipeline dashboard with no-show & conversion analytics",
    ],
  },
  {
    slug: "notary-calc-pro",
    icon: Calculator,
    name: "NotaryCalcPro",
    tagline: "Flat-rate & travel-fee quoting calculator",
    isAvailable: true,
    downloadUrl: "https://notarycalcpro.com",
    features: [
      "State-compliant statutory fee calculator (per signature)",
      "Auto-computed travel, after-hours, and rush surcharges",
      "Signing-package presets (refi, purchase, reverse, HELOC, RON)",
      "One-click quote PDF for client and vendor delivery",
    ],
  },
  {
    slug: "app-expensebookpro",
    icon: Wallet,
    name: "ExpenseBookPro",
    tagline: "Field-friendly expense & mileage tracker",
    features: [
      "Mileage auto-logging for every signing assignment",
      "Receipt capture with category tagging (supplies, fuel, fees)",
      "Quarterly tax-ready exports (CSV + IRS-friendly summaries)",
      "Schedule-C and 1099-NEC export views for tax season",
    ],
  },
  {
    slug: "app-notarycoachpro",
    icon: GraduationCap,
    name: "NotaryCoachPro",
    tagline: "Compliance & signing desktop companion",
    features: [
      "Pick your state + document — the State Compliance Shield instantly flags red (critical) vs blue (standard) requirements",
      "Dynamic compliance blueprint: signer requirements, notary tasks, seal/stamp placement, critical vs conditional data fields, workflow & returns",
      "Interactive verbal scripts (notification, table walkthrough, closing) — professional cadence without giving unauthorized legal advice",
      "15 foundational document templates + custom-document search; built for notaries, real estate agents, trust/estate attorneys, mortgage brokers, and senior care specialists",
    ],
  },
  {
    slug: "app-notarwiz",
    icon: PlaneTakeoff,
    name: "NotarWiz",
    tagline: "Pre-flight signing scripter — built by a notary, for notaries",
    features: [
      "Upload any signing package PDF (up to 200 pages) and get a printable page-by-page flight plan in 30–120 seconds",
      "Every signature line, initial, notarial act, and stamp placement is called out with exact page, role, and location — never miss a blank",
      "Signing-table ready: print as your live worksheet or check actions off on your phone; progress persists per document",
      "Plain-English abstracts of every major instrument (Note, Deed of Trust, Certification of Trust, Closing Disclosure) — walk in ready to answer signer questions",
    ],
  },
];

// Map an app-card record into the shape the shared LeadCaptureModal expects.
// If the app is available, we omit `comingSoon` so the modal treats it as a
// real gated download and the LeadCaptureModal renders a real launch link.
function toModalShape(app) {
  if (app.isAvailable) {
    return {
      slug: app.slug,
      kind: "APP",
      icon: app.icon,
      title: `Get ${app.name}`,
      body: `${app.tagline}. Enter your first name and email — we'll unlock the launch link and open ${app.name} in a new tab.`,
      cta: "Unlock & Open",
      resourceLabel: `App: ${app.name}`,
      source: "apps_page_download",
    };
  }
  return {
    slug: app.slug,
    kind: "APP",
    icon: app.icon,
    title: `Join the ${app.name} Waitlist`,
    body: `${app.tagline}. We'll email you at the address below the moment ${app.name} is available. No spam, no marketing lists — one email, one launch notice.`,
    cta: "Join the Waitlist",
    resourceLabel: `App: ${app.name}`,
    source: "apps_page_waitlist",
    comingSoon: true,
  };
}

export default function AppsPage() {
  const [modalResource, setModalResource] = React.useState(null);
  const { isAuthed, authHeader } = useAuth();

  // Dual-path handler for AVAILABLE apps: logged-in members bypass the modal
  // and get an instant redirect + download-log entry; guests still capture
  // name + email through the shared LeadCaptureModal.
  const handleAppRequest = React.useCallback(
    async (app) => {
      if (!app.isAvailable) {
        setModalResource(toModalShape(app));
        return;
      }
      if (!isAuthed) {
        setModalResource(toModalShape(app));
        return;
      }
      try {
        const { data } = await axios.post(
          `${API}/community/downloads/log`,
          { slug: app.slug },
          { headers: authHeader() }
        );
        const url = data?.download_url || app.downloadUrl;
        if (url) {
          openDownload(url);
          toast.success("Opening " + app.name, {
            description: "Enjoy — logged as a member download.",
            id: `app-${app.slug}`,
          });
        } else {
          toast.error("That app isn't ready yet.");
        }
      } catch (err) {
        toast.error(
          err?.response?.data?.detail ||
            "Couldn't open that app right now — please try again."
        );
      }
    },
    [isAuthed, authHeader]
  );

  return (
    <>
      <PageHero
        eyebrow="OGO Tech & Safety Applications"
        title="Software companions to the practice — coming soon."
        lead="A growing toolkit of digital products purpose-built for the modern notary, signing agent, and solopreneur. Each tool is engineered with the same rigor we bring to the signing table — secure, compliant, and built to make your day faster, leaner, and more profitable."
      />

      <section className="py-24 sm:py-32 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {APPS.map((a) => (
              <AppCard
                key={a.slug}
                app={a}
                onWaitlist={() => handleAppRequest(a)}
              />
            ))}
            <ManyMoreCard />
          </div>
        </div>
      </section>

      <LeadCaptureModal
        resource={modalResource}
        onClose={() => setModalResource(null)}
      />
    </>
  );
}

function AppCard({ app, onWaitlist }) {
  const Icon = app.icon;
  const available = !!app.isAvailable;
  return (
    <div
      data-testid={`app-card-${app.name.toLowerCase()}`}
      className={`border ${available ? "border-[#D4AF37]/60" : "border-white/10"} bg-[#1A2B45] p-8 sm:p-10 hover:border-[#D4AF37]/60 transition-colors flex flex-col`}
    >
      <div className="flex items-start justify-between mb-6">
        <div className="w-12 h-12 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37]">
          <Icon className="w-6 h-6" />
        </div>
        <span
          className={`text-[10px] uppercase tracking-[0.22em] px-2 py-1 border ${
            available
              ? "text-[#142339] bg-[#D4AF37] border-[#D4AF37] font-bold"
              : "text-slate-500 border-white/10"
          }`}
        >
          {available ? "Available Now" : "In Development"}
        </span>
      </div>

      <h3 className="font-serif-display text-3xl text-white mb-2 leading-tight">
        {app.name}
      </h3>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-6">
        {app.tagline}
      </div>

      <ul className="space-y-3 mb-8 flex-1">
        {app.features.map((f, i) => (
          <li key={i} className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-1.5 w-1.5 h-1.5 shrink-0 bg-[#D4AF37]"
            />
            <span className="text-slate-200 text-sm leading-relaxed">{f}</span>
          </li>
        ))}
      </ul>

      {available ? (
        <button
          type="button"
          onClick={onWaitlist}
          data-testid={`app-download-cta-${app.name.toLowerCase()}`}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#142339] hover:bg-[#FDE047] px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-bold transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
          Download Now
          <ExternalLink className="w-3 h-3 opacity-70" />
        </button>
      ) : (
        <button
          type="button"
          onClick={onWaitlist}
          data-testid={`app-waitlist-cta-${app.name.toLowerCase()}`}
          className="w-full inline-flex items-center justify-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-bold transition-colors"
        >
          <Mail className="w-3.5 h-3.5" />
          Join the Waitlist
        </button>
      )}
    </div>
  );
}

function ManyMoreCard() {
  return (
    <div
      data-testid="app-card-many-more"
      className="relative border border-dashed border-[#D4AF37]/40 bg-[#1A2B45]/40 p-8 sm:p-10 flex flex-col justify-center text-center"
    >
      <div className="w-14 h-14 border border-[#D4AF37] flex items-center justify-center text-[#D4AF37] mx-auto mb-6">
        <Sparkles className="w-7 h-7" />
      </div>
      <SectionEyebrow className="mb-4">More On the Way</SectionEyebrow>
      <h3 className="font-serif-display text-2xl sm:text-3xl text-white mb-4 leading-tight">
        And many others being developed.
      </h3>
      <p className="text-slate-300 leading-relaxed text-sm max-w-md mx-auto">
        Built to make the{" "}
        <span className="text-white">Solo/Small Notary Business</span>,{" "}
        <span className="text-white">Solopreneur</span>, or{" "}
        <span className="text-white">Small Minority Business</span> more
        compliant, more capable, and more profitable.
      </p>
    </div>
  );
}
